/**
 * Wizard 2.0 — plan a campaign in four screens.
 *
 * The old planner made users produce (edit SWOTs, write personas) across seven
 * AI-gated steps. This one makes them decide: Intent (one prefilled screen) →
 * Plan (agent-assembled cards to approve or swap) → Media plan (split + casting
 * spec) → Go (Saga builds the real campaign). Creators are deliberately NOT
 * picked here — the wizard approves a casting spec; the person-by-person
 * decision happens at the curation gate, where it always was.
 * See cowork-cloud docs/WIZARD-2.0-DESIGN.md.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { NumberField } from '@/cowork/shared/NumberField';
import { PromotablePicker, PickedPromotable, promotableContext } from '@/cowork/proposals/PromotablePicker';
import type { Promotable } from '@/cowork/omniad/types/cowork';
import { useNavigate } from 'react-router-dom';
import {
  X, ArrowRight, ArrowLeft, Megaphone, MousePointerClick, HeartHandshake,
  Magnet, Smartphone, ShoppingCart, Sparkles, Users, Lightbulb, Check,
  Loader2, Rocket, type LucideIcon,
} from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { MarketPicker } from '@/components/MarketPicker';
import {
  coworkApi, type WizardPrepare, type WizardIntent, type WizardPlan,
} from '@/cowork/omniad/api/coworkApi';
import { useEffectiveBrand } from '@/cowork/useActiveBrand';

const OBJECTIVE_ICON: Record<string, LucideIcon> = {
  megaphone: Megaphone,
  'mouse-pointer-click': MousePointerClick,
  'heart-handshake': HeartHandshake,
  magnet: Magnet,
  smartphone: Smartphone,
  'shopping-cart': ShoppingCart,
};

const SCREENS = ['Intent', 'Plan', 'Media plan', 'Go'] as const;

export function CampaignWizard() {
  const navigate = useNavigate();
  const { brandId, brandName } = useEffectiveBrand();

  const [screen, setScreen] = useState(0);
  const [prep, setPrep] = useState<WizardPrepare | null>(null);

  // Screen 1 — intent
  const [objective, setObjective] = useState<WizardIntent['objective'] | null>(null);
  const [budget, setBudget] = useState<string>('');
  const [market, setMarket] = useState<string>('');
  const [product, setProduct] = useState('');
  const [pickedPromotable, setPickedPromotable] = useState<Promotable | null>(null);
  const [notes, setNotes] = useState('');

  // Screen 2/3 — the generated plan and the user's edits to it
  const [plan, setPlan] = useState<WizardPlan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [conceptIdx, setConceptIdx] = useState(0);
  const [audienceOn, setAudienceOn] = useState<boolean[]>([]);
  const [splits, setSplits] = useState<Record<string, number>>({});
  const [castCount, setCastCount] = useState<number>(0);
  const [castBand, setCastBand] = useState('');

  // Screen 4
  const [campaignName, setCampaignName] = useState('');
  const [handing, setHanding] = useState(false);
  const [done, setDone] = useState<{ taskId: string; name: string } | null>(null);

  useEffect(() => {
    /* Chat → wizard bridge: an agent that gathered intent conversationally
     * hands the user here with it in the URL — chat captures, the wizard
     * reviews. Explicit params beat history-derived defaults. */
    const q = new URLSearchParams(window.location.search);
    const qObjective = q.get('objective')?.toUpperCase();
    const qBudget = q.get('budget');
    const qMarket = q.get('market');
    if (q.get('product')) setProduct(q.get('product')!);
    if (q.get('notes')) setNotes(q.get('notes')!);

    coworkApi.wizard.prepare().then(p => {
      setPrep(p);
      const validObjective = p.objectives.find(o => o.code === qObjective)?.code;
      if (validObjective) setObjective(validObjective);
      if (qBudget && Number(qBudget) > 0) setBudget(qBudget);
      else if (p.defaults.budget) setBudget(String(p.defaults.budget));
      setMarket(
        (qMarket && p.markets.find(m => m.toLowerCase() === qMarket.toLowerCase()))
          || p.defaults.market || p.markets[0] || 'Sweden',
      );
    }).catch(() => setPrep({ objectives: [], channels: [], markets: [], defaults: {} }));
  }, []);

  const intent: WizardIntent | null = useMemo(() => {
    const b = Number(budget);
    if (!objective || !Number.isFinite(b) || b <= 0 || !market) return null;
    return {
      objective, budget: b, market,
      product: product.trim() || undefined,
      productContext: pickedPromotable ? promotableContext(pickedPromotable) : undefined,
      notes: notes.trim() || undefined,
    };
  }, [objective, budget, market, product, pickedPromotable, notes]);

  /* Prefetch: generation starts the moment Screen 1 is valid, debounced so
   * typing a budget doesn't fire a call per keystroke. By the time the user
   * clicks Continue the plan is usually already resolved — the skeletons on
   * Screen 2 are the fallback, not the norm. Keyed on the exact intent, so an
   * edit invalidates the prefetch and Back/Continue reuses it. */
  const prefetch = useRef<{ key: string; promise: Promise<WizardPlan> } | null>(null);

  function planPromiseFor(i: WizardIntent): Promise<WizardPlan> {
    const key = JSON.stringify({ brandId, i });
    if (prefetch.current?.key !== key) {
      prefetch.current = {
        key,
        promise: coworkApi.wizard.generatePlan({ brandId: brandId!, brandName, intent: i }).then(r => r.plan),
      };
    }
    return prefetch.current.promise;
  }

  useEffect(() => {
    if (!intent || !brandId || screen !== 0) return;
    const t = setTimeout(() => { void planPromiseFor(intent).catch(() => { /* surfaced on Screen 2 */ }); }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- planPromiseFor is stable in what matters (brandId/intent are in the key)
  }, [intent, brandId, screen]);

  function startGeneration() {
    if (!intent || !brandId) return;
    setScreen(1);
    setPlan(null);
    setPlanError(null);
    planPromiseFor(intent)
      .then(p => {
        setPlan(p);
        setAudienceOn(p.audiences.map(() => true));
        setConceptIdx(0);
        setSplits(Object.fromEntries(p.mediaplan.map(m => [m.channelId, m.percent])));
        setCastCount(p.castingSpec.count);
        setCastBand(p.castingSpec.sizeBand);
      })
      .catch(() => {
        prefetch.current = null; // a failed prefetch must not be reused
        setPlanError('Plan generation failed: go back and try again.');
      });
  }

  const splitTotal = Object.values(splits).reduce((a, b) => a + b, 0);

  async function go() {
    if (!intent || !brandId || !plan || handing) return;
    setHanding(true);
    try {
      const orderedConcepts = [
        plan.concepts[conceptIdx],
        ...plan.concepts.filter((_, i) => i !== conceptIdx),
      ];
      const finalPlan: WizardPlan = {
        ...plan,
        audiences: plan.audiences.filter((_, i) => audienceOn[i]),
        concepts: orderedConcepts,
        castingSpec: { ...plan.castingSpec, count: castCount, sizeBand: castBand },
        mediaplan: plan.mediaplan.map(m => ({ ...m, percent: splits[m.channelId] ?? m.percent })),
      };
      const res = await coworkApi.wizard.handoff({
        brandId, brandName, intent, plan: finalPlan,
        campaignName: campaignName.trim() || undefined,
      });
      setDone(res);
    } catch {
      setHanding(false);
    }
  }

  const canContinue = [
    intent !== null,
    plan !== null,
    plan !== null && splitTotal === 100 && castCount > 0,
    true,
  ][screen];

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-[#f8f9fa]">
      {/* Header: progress + close */}
      <header className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4.5 w-4.5 text-[#be43ff]" aria-hidden />
          <span className="text-[15px] text-[#101828]" style={{ fontWeight: 700 }}>
            New campaign{brandName ? `: ${brandName}` : ''}
          </span>
        </div>
        <nav className="hidden items-center gap-1 md:flex" aria-label="Wizard progress">
          {SCREENS.map((s, i) => (
            <div key={s} className="flex items-center gap-1">
              <button
                type="button"
                disabled={i > screen}
                onClick={() => i < screen && setScreen(i)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors',
                  i === screen ? 'bg-[#be43ff]/10 text-[#be43ff]'
                    : i < screen ? 'text-[#12b76a] hover:bg-gray-50' : 'text-[#98a2b3]',
                )}
                style={{ fontWeight: 600 }}
              >
                {i < screen ? <Check className="h-3 w-3" aria-hidden /> : <span>{i + 1}</span>}
                {s}
              </button>
              {i < SCREENS.length - 1 && <div className="h-px w-4 bg-gray-200" />}
            </div>
          ))}
        </nav>
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label="Close wizard"
          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-4.5 w-4.5" aria-hidden />
        </button>
      </header>

      {/* Body */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-6 py-8">

          {/* ── Screen 1: Intent ── */}
          {screen === 0 && prep && (
            <div className="space-y-7">
              <div>
                <h1 className="text-xl text-[#101828]" style={{ fontWeight: 700 }}>What should this campaign do?</h1>
                <p className="mt-1 text-sm text-[#667085]">Everything below is prefilled from your last campaign: change what's different this time.</p>
              </div>

              <fieldset>
                <legend className="mb-2.5 text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>Objective</legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {prep.objectives.map(o => {
                    const Icon = OBJECTIVE_ICON[o.icon] ?? Megaphone;
                    const active = objective === o.code;
                    return (
                      <button
                        key={o.code}
                        type="button"
                        onClick={() => setObjective(o.code)}
                        className={cn(
                          'rounded-xl border p-3.5 text-left transition-all',
                          active ? 'border-[#be43ff] bg-[#be43ff]/[0.04] shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300',
                        )}
                        aria-pressed={active}
                      >
                        <div className="mb-1.5 flex items-center gap-2">
                          <Icon className={cn('h-4 w-4', active ? 'text-[#be43ff]' : 'text-gray-400')} aria-hidden />
                          <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{o.name}</span>
                        </div>
                        <p className="text-xs leading-relaxed text-[#667085]">{o.text}</p>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>Budget (SEK)</span>
                  <NumberField
                    min={1} group allowEmpty value={budget === '' ? null : Number(budget)} onChange={n => setBudget(n == null ? '' : String(n))}
                    placeholder="200 000"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#be43ff]"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>Market</span>
                  <MarketPicker value={market} onChange={setMarket} extra={prep.markets} className="w-full px-3 text-sm" data-testid="wizard-market" />
                </label>
              </div>

              <div className="block">
                <div className="mb-1.5 flex items-baseline justify-between"><span className="block text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>Product or offer <span className="text-[#98a2b3]" style={{ fontWeight: 400 }}>(optional)</span></span><PromotablePicker brandId={brandId} onPick={(p) => { setPickedPromotable(p); setProduct(p.name); }} /></div>
                <input
                  value={product} onChange={e => { setPickedPromotable(null); setProduct(e.target.value); }}
                  placeholder="What are we promoting?"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#be43ff]"
                />
                {pickedPromotable && <PickedPromotable p={pickedPromotable} onClear={() => setPickedPromotable(null)} />}
              </div>
              <label className="block">
                <span className="mb-1.5 block text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>Anything else the plan should respect <span className="text-[#98a2b3]" style={{ fontWeight: 400 }}>(optional)</span></span>
                <textarea
                  value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  placeholder="Timing constraints, launch tie-ins, no-go areas…"
                  className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#be43ff]"
                />
              </label>
            </div>
          )}
          {screen === 0 && !prep && (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#be43ff]" aria-label="Loading" /></div>
          )}

          {/* ── Screen 2: Plan ── */}
          {screen === 1 && (
            <div className="space-y-7">
              <div>
                <h1 className="text-xl text-[#101828]" style={{ fontWeight: 700 }}>Here's the plan</h1>
                <p className="mt-1 text-sm text-[#667085]">Assembled from what we know about {brandName || 'this brand'}, approve or swap, nothing to write.</p>
              </div>

              {!plan && !planError && (
                <div className="space-y-3" aria-label="Generating plan">
                  {[0, 1, 2].map(i => <div key={i} className="h-28 animate-pulse rounded-xl bg-gray-200/60" />)}
                  <p className="text-center text-xs text-[#98a2b3]">Reading the Client Brain and drafting the plan…</p>
                </div>
              )}
              {planError && <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{planError}</p>}

              {plan && (
                <>
                  <section className="rounded-xl border border-gray-200 bg-white p-4">
                    <h2 className="mb-1.5 text-[13px] uppercase tracking-wide text-[#98a2b3]" style={{ fontWeight: 600 }}>Market snapshot</h2>
                    <p className="text-sm leading-relaxed text-[#344054]">{plan.marketSnapshot}</p>
                  </section>

                  <section>
                    <h2 className="mb-2.5 flex items-center gap-1.5 text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>
                      <Users className="h-3.5 w-3.5 text-[#be43ff]" aria-hidden /> Audience: untick any that don't fit
                    </h2>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {plan.audiences.map((a, i) => (
                        <button
                          key={a.title} type="button"
                          onClick={() => setAudienceOn(v => v.map((x, j) => j === i ? !x : x))}
                          aria-pressed={audienceOn[i]}
                          className={cn(
                            'rounded-xl border p-3.5 text-left transition-all',
                            audienceOn[i] ? 'border-[#be43ff]/50 bg-white shadow-sm' : 'border-gray-200 bg-gray-50 opacity-60',
                          )}
                        >
                          <div className="mb-1 flex items-center justify-between">
                            <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{a.title}</span>
                            {audienceOn[i] && <Check className="h-3.5 w-3.5 text-[#be43ff]" aria-hidden />}
                          </div>
                          <p className="text-xs leading-relaxed text-[#667085]">{a.description}</p>
                          <p className="mt-1.5 text-[11px] italic text-[#98a2b3]">{a.why}{a.fromWiki ? ' · from your Client Brain' : ''}</p>
                        </button>
                      ))}
                    </div>
                  </section>

                  <section>
                    <h2 className="mb-2.5 flex items-center gap-1.5 text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>
                      <Lightbulb className="h-3.5 w-3.5 text-[#be43ff]" aria-hidden /> Concept: pick one
                    </h2>
                    <div className="space-y-2">
                      {plan.concepts.map((c, i) => (
                        <button
                          key={c.title} type="button" onClick={() => setConceptIdx(i)}
                          aria-pressed={conceptIdx === i}
                          className={cn(
                            'w-full rounded-xl border p-4 text-left transition-all',
                            conceptIdx === i ? 'border-[#be43ff] bg-[#be43ff]/[0.04] shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300',
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-[#101828]" style={{ fontWeight: 600 }}>{c.title}</span>
                            {conceptIdx === i && <Check className="h-4 w-4 text-[#be43ff]" aria-hidden />}
                          </div>
                          <p className="mt-0.5 text-[13px] text-[#be43ff]">"{c.hook}"</p>
                          <p className="mt-1.5 text-xs leading-relaxed text-[#667085]">{c.description}</p>
                          <p className="mt-1.5 text-[11px] text-[#98a2b3]">{c.formats.join(' · ')}: {c.why}</p>
                        </button>
                      ))}
                    </div>
                  </section>
                </>
              )}
            </div>
          )}

          {/* ── Screen 3: Media plan + casting spec ── */}
          {screen === 2 && plan && prep && (
            <div className="space-y-7">
              <div>
                <h1 className="text-xl text-[#101828]" style={{ fontWeight: 700 }}>Where the budget goes</h1>
                <p className="mt-1 text-sm text-[#667085]">Starting points come from your objective and past learnings. Total must land on 100%.</p>
              </div>

              <section className="space-y-3">
                {plan.mediaplan.map(m => {
                  const ch = prep.channels.find(c => c.id === m.channelId);
                  const pct = splits[m.channelId] ?? m.percent;
                  return (
                    <div key={m.channelId} className="rounded-xl border border-gray-200 bg-white p-4">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{ch?.name ?? m.channelId}</span>
                        <span className="text-sm tabular-nums text-[#be43ff]" style={{ fontWeight: 700 }}>{pct}%</span>
                      </div>
                      <input
                        type="range" min={0} max={100} step={5} value={pct}
                        onChange={e => setSplits(s => ({ ...s, [m.channelId]: Number(e.target.value) }))}
                        className="w-full accent-[#be43ff]"
                        aria-label={`${ch?.name ?? m.channelId} share`}
                      />
                      <p className="mt-1 text-xs text-[#667085]">{m.rationale}</p>
                    </div>
                  );
                })}
                <p className={cn('text-right text-xs tabular-nums', splitTotal === 100 ? 'text-[#12b76a]' : 'text-red-500')} style={{ fontWeight: 600 }}>
                  Total {splitTotal}%{splitTotal !== 100 && ': adjust to 100%'}
                </p>
              </section>

              <section className="rounded-xl border border-[#be43ff]/30 bg-[#be43ff]/[0.03] p-4">
                <h2 className="mb-2 text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>Casting spec: you'll approve the actual creators later</h2>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="mb-1 block text-[11px] uppercase tracking-wide text-[#98a2b3]" style={{ fontWeight: 600 }}>Creators to source</span>
                    <NumberField
                      min={1} value={castCount}
                      onChange={n => setCastCount(n ?? 1)}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-[#be43ff]"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-[11px] uppercase tracking-wide text-[#98a2b3]" style={{ fontWeight: 600 }}>Size band</span>
                    <input
                      value={castBand} onChange={e => setCastBand(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-[#be43ff]"
                    />
                  </label>
                </div>
                <p className="mt-2.5 text-xs text-[#667085]">
                  {plan.castingSpec.niches.join(', ')} · {plan.castingSpec.language} · {plan.castingSpec.mustHave}
                </p>
                <p className="mt-2 text-[11px] text-[#98a2b3]">
                  Rex sources against this spec, oversourced so you have real choice. The person-by-person decision happens on the campaign's Profiles tab: it will appear in "What needs you" when the list is ready.
                </p>
              </section>
            </div>
          )}

          {/* ── Screen 4: Go ── */}
          {screen === 3 && plan && intent && !done && (
            <div className="space-y-7">
              <div>
                <h1 className="text-xl text-[#101828]" style={{ fontWeight: 700 }}>Ready to build</h1>
                <p className="mt-1 text-sm text-[#667085]">Saga creates the campaign (draft, beats, brief, creator sourcing) and stops at every gate for you.</p>
              </div>
              <label className="block">
                <span className="mb-1.5 block text-[13px] text-[#344054]" style={{ fontWeight: 600 }}>Campaign name</span>
                <input
                  value={campaignName} onChange={e => setCampaignName(e.target.value)}
                  placeholder={`${brandName || 'Campaign'}: ${intent.market} ${new Date().toISOString().slice(0, 7)}`}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#be43ff]"
                />
              </label>
              <section className="rounded-xl border border-gray-200 bg-white p-4 text-sm leading-relaxed text-[#344054]">
                <p><strong>{intent.objective}</strong> · {Number(intent.budget).toLocaleString('sv-SE')} SEK · {intent.market}</p>
                <p className="mt-1.5"><strong>Concept:</strong> {plan.concepts[conceptIdx]?.title}: "{plan.concepts[conceptIdx]?.hook}"</p>
                <p className="mt-1.5"><strong>Audience:</strong> {plan.audiences.filter((_, i) => audienceOn[i]).map(a => a.title).join(', ') || '—'}</p>
                <p className="mt-1.5"><strong>Casting:</strong> ~{castCount} creators, {castBand}</p>
                <p className="mt-1.5"><strong>Split:</strong> {plan.mediaplan.map(m => `${m.channelId} ${splits[m.channelId] ?? m.percent}%`).join(' · ')}</p>
              </section>
              <p className="text-xs text-[#98a2b3]">Nothing goes live from this screen: launching stays a decision you make on the campaign itself.</p>
            </div>
          )}

          {done && (
            <div className="flex flex-col items-center gap-4 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#12b76a]/10">
                <Rocket className="h-7 w-7 text-[#12b76a]" aria-hidden />
              </span>
              <div>
                <h1 className="text-xl text-[#101828]" style={{ fontWeight: 700 }}>Saga is building "{done.name}"</h1>
                <p className="mx-auto mt-1.5 max-w-md text-sm text-[#667085]">
                  Draft, beats, brief, then creator sourcing. When the creator list is ready it appears in "What needs you": that decision is yours.
                </p>
              </div>
              <div className="flex gap-2.5">
                <button type="button" onClick={() => navigate('/agents/tasks')} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-[#344054] transition-colors hover:border-gray-300" style={{ fontWeight: 600 }}>
                  Watch progress
                </button>
                <button type="button" onClick={() => navigate('/')} className="rounded-lg bg-[#be43ff] px-4 py-2 text-sm text-white transition-colors hover:bg-[#a832e6]" style={{ fontWeight: 600 }}>
                  Back to Home
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer nav */}
      {!done && (
        <footer className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-3.5">
          <button
            type="button"
            onClick={() => screen > 0 ? setScreen(s => s - 1) : navigate('/')}
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-[#667085] transition-colors hover:bg-gray-50"
            style={{ fontWeight: 600 }}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden /> {screen === 0 ? 'Cancel' : 'Back'}
          </button>
          <button
            type="button"
            disabled={!canContinue || handing}
            onClick={() => {
              if (screen === 0) startGeneration();
              else if (screen === 3) void go();
              else setScreen(s => s + 1);
            }}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-5 py-2 text-sm text-white transition-colors',
              canContinue && !handing ? 'bg-[#be43ff] hover:bg-[#a832e6]' : 'cursor-not-allowed bg-gray-300',
            )}
            style={{ fontWeight: 600 }}
          >
            {handing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            {screen === 3 ? (handing ? 'Handing to Saga…' : 'Build it') : 'Continue'}
            {screen < 3 && <ArrowRight className="h-4 w-4" aria-hidden />}
          </button>
        </footer>
      )}
    </div>
  );
}
